import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';
import { isMissingRoute } from './ehrMapThisApi';
import type { EhrAllergyChangeCount, EhrAllergyChangeList, EhrAllergyReviewResult } from '../types/ehrAllergyReview';

// React Query hooks for EHR > Allergy Changes (EhrAllergyReviewController).

export const allergyReviewKeys = {
  all: ['ehrAllergyReview'] as const,
  list: (facilityId: number, reviewed: boolean) => [...allergyReviewKeys.all, 'list', facilityId, reviewed] as const,
  count: (facilityId: number) => [...allergyReviewKeys.all, 'count', facilityId] as const,
};

/** How often the tab's badge asks again. */
export const ALLERGY_COUNT_REFRESH_MS = 2 * 60 * 1000;

export function useAllergyChanges(facilityId: number, reviewed: boolean, enabled = true) {
  return useQuery({
    queryKey: allergyReviewKeys.list(facilityId, reviewed),
    queryFn: async () =>
      (await apiClient.get<EhrAllergyChangeList>(API.ehrAllergyReviewList(facilityId, reviewed), { _skipVersionMismatch: true } as never)).data,
    enabled: enabled && facilityId > 0,
    retry: (count, error) => !isMissingRoute(error) && count < 2,
  });
}

/** The changes waiting for review: the Allergy Changes tab's badge. Quiet on a 404 (an older API slot). */
export function useAllergyChangeCount(facilityId: number, enabled = true, queryClient?: QueryClient) {
  return useQuery({
    queryKey: allergyReviewKeys.count(facilityId),
    queryFn: async () =>
      (await apiClient.get<EhrAllergyChangeCount>(API.ehrAllergyReviewCount(facilityId), { _skipVersionMismatch: true } as never)).data,
    enabled: enabled && facilityId > 0,
    refetchInterval: ALLERGY_COUNT_REFRESH_MS,
    staleTime: 60 * 1000,
    retry: (count, error) => !isMissingRoute(error) && count < 1,
  }, queryClient);
}

export function useMarkAllergyReviewed(facilityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: number[]) =>
      (await apiClient.post<EhrAllergyReviewResult>(API.ehrAllergyReviewMark(facilityId), { ids })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: allergyReviewKeys.all }),
  });
}
